import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import * as api from "../api";
import { options as dateOptions } from "../utils/dateFormat";
import TableSeatRow from "../components/Tables/TableSeatRow";
import { ReactComponent as SeatsIcon } from "../assets/images/users.svg";
import { ReactComponent as ClockIcon } from "../assets/images/clock.svg";
import { ReactComponent as AlertIcon } from "../assets/images/input-error.svg";

/**
 * The host's roster screen.
 *
 * Reachable two ways, which differ only in how the request is authorised:
 *   /tables/manage/:token  -- public; the token IS the credential, so a VIP
 *                             client with no account can manage their table
 *   /tables/:code          -- authenticated; linked from /orders
 *
 * The response shape is identical either way, so nothing below branches on it
 * beyond choosing the request and passing the token down to the seat rows.
 */

/** Deadline copy needs the time of day; the event date does not. */
const lockOptions = {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

const formatDate = (value, formatOptions) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString([], formatOptions);
};

const ManageTable = () => {
  const { code, token } = useParams();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [slow, setSlow] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setSlow(false);
    const request = token
      ? api.tables.getTableByToken(token)
      : api.tables.getTable(code);
    request
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          setTable(response.data);
          setLoadError("");
        } else {
          setLoadError("We couldn't load this table.");
        }
        setLoading(false);
      })
      .catch(() => {
        setLoadError("We couldn't load this table.");
        setLoading(false);
      });
  }, [code, token]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * A seat changed.
   *
   * Applies the seat the server returned straight away, then reloads for the
   * aggregates only this endpoint can compute -- seats named, ratio, the
   * assigned label. Waiting on the reload alone meant a change the server had
   * already made did not show until the host refreshed the page, and a reload
   * that failed lost it entirely.
   */
  const handleSeatChanged = useCallback(
    (updatedSeat) => {
      if (updatedSeat && updatedSeat.position != null) {
        setTable((current) =>
          current
            ? {
                ...current,
                seats: (current.seats || []).map((seat) =>
                  seat.position === updatedSeat.position ? updatedSeat : seat,
                ),
              }
            : current,
        );
      }
      load();
    },
    [load],
  );

  // A skeleton that never resolves is indistinguishable from a broken page, so
  // a stalled request eventually says so and offers a way out.
  useEffect(() => {
    if (!loading) {
      setSlow(false);
      return undefined;
    }
    const timer = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(timer);
  }, [loading]);

  // A skeleton rather than a spinner: the roster's shape is known before its
  // contents are, so the page does not jump when the data lands.
  if (loading && !table) {
    return (
      <div className="manage-table">
        <span className="manage-table__eyebrow">Your table</span>
        <div aria-hidden="true">
          <div className="manage-table__skeleton manage-table__skeleton--title" />
          <div className="manage-table__skeleton manage-table__skeleton--subtitle" />
          <div className="manage-table__panel">
            <div className="manage-table__panel-top">
              <div className="manage-table__skeleton manage-table__skeleton--chip" />
              <div className="manage-table__skeleton manage-table__skeleton--count" />
            </div>
            <div className="manage-table__progress" />
            <div className="manage-table__stats">
              <div className="manage-table__skeleton manage-table__skeleton--stat" />
              <div className="manage-table__skeleton manage-table__skeleton--stat" />
            </div>
          </div>
          <div className="manage-table__section">
            <div className="manage-table__skeleton manage-table__skeleton--section" />
          </div>
          <div className="manage-table__seats">
            {[0, 1, 2, 3].map((row) => (
              <div className="table-seat" key={row}>
                <div className="table-seat__main">
                  <div className="manage-table__skeleton manage-table__skeleton--avatar" />
                  <div className="table-seat__identity">
                    <div className="manage-table__skeleton manage-table__skeleton--name" />
                    <div className="manage-table__skeleton manage-table__skeleton--line" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="manage-table__sr-only" role="status">
          Loading your guest list…
        </p>
        {slow ? (
          <div className="manage-table__state" role="status">
            <div className="manage-table__state-title">
              This is taking longer than usual
            </div>
            <p className="manage-table__state-body">
              Your table is still loading. Check your connection, or try again.
            </p>
            <button
              type="button"
              className="manage-table__retry"
              onClick={load}
            >
              Try again
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  // Only a first load that fails takes over the screen. A reload that fails --
  // and every seat change triggers one -- must not throw away a roster the host
  // is halfway through filling.
  if (loadError && !table) {
    return (
      <div className="manage-table">
        <span className="manage-table__eyebrow">Your table</span>
        <h2 className="page-title">Guest list</h2>
        <div className="manage-table__state" role="alert">
          <div className="manage-table__state-title">
            We couldn't load this table
          </div>
          <p className="manage-table__state-body">
            The link may have expired, or the connection dropped. Try again, and
            if it keeps failing, contact whoever sold you the table.
          </p>
          <button type="button" className="manage-table__retry" onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!table) return null;

  const seats = table.seats || [];
  const filled = table.capacity - table.seatsRemaining;
  const filledRatio = table.capacity > 0 ? filled / table.capacity : 0;
  const locksAt = formatDate(table.rosterLocksAt, lockOptions);
  const startsAt = formatDate(table.eventStartsAt, dateOptions);
  const isCanceled = Boolean(table.canceledAt);
  const rosterClosed =
    Boolean(table.rosterLocksAt) && new Date(table.rosterLocksAt) <= new Date();

  return (
    <div className="manage-table">
      <span className="manage-table__eyebrow">Your table</span>
      <h2 className="page-title">{table.eventName}</h2>
      {table.tierName || startsAt ? (
        <p className="manage-table__subtitle">
          {[table.tierName, startsAt].filter(Boolean).join(" · ")}
        </p>
      ) : null}

      <div className="manage-table__panel">
        <div className="manage-table__panel-top">
          <span className="manage-table__code">Table {table.code}</span>
          <span className="manage-table__count">
            <span className="manage-table__count-value">
              {filled}/{table.capacity}
            </span>
            <span className="manage-table__count-label">seats named</span>
          </span>
        </div>

        <div
          className="manage-table__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={table.capacity}
          aria-valuenow={filled}
          aria-valuetext={`${filled} of ${table.capacity} seats named`}
        >
          <div
            className="manage-table__progress-fill"
            style={{ transform: `scaleX(${filledRatio})` }}
          />
        </div>

        <div className="manage-table__stats">
          <span className="manage-table__stat">
            <SeatsIcon aria-hidden="true" />
            {table.seatsRemaining > 0
              ? `${table.seatsRemaining} still open`
              : "Every seat named"}
          </span>
          {/* The deadline, always stated -- a host planning who to invite needs
              to know how long they have, and "no deadline" is itself an answer.
              A null roster_locks_at means editable until the guest arrives,
              which is what freezes a seat (TableSeat.is_checked_in). */}
          <span className="manage-table__stat">
            <ClockIcon aria-hidden="true" />
            {locksAt ? `Guest list closes ${locksAt}` : "Open until check-in"}
          </span>
          {/* Shown because door policy depends on it, and the backend computes
              it per table from the named seats. */}
          {table.ratio ? (
            <span className="manage-table__stat">Ratio {table.ratio}</span>
          ) : null}
          {table.assignedLabel ? (
            <span className="manage-table__stat">{table.assignedLabel}</span>
          ) : null}
        </div>
      </div>

      {loadError ? (
        <div
          className="manage-table__notice manage-table__notice--danger"
          role="alert"
        >
          <AlertIcon aria-hidden="true" />
          <span>
            {loadError} What you see below may be out of date.{" "}
            <button
              type="button"
              className="manage-table__inline-retry"
              onClick={load}
            >
              Refresh
            </button>
          </span>
        </div>
      ) : null}

      {isCanceled ? (
        <div className="manage-table__notice manage-table__notice--danger">
          <AlertIcon aria-hidden="true" />
          <span>
            This table has been canceled, so its guest list can no longer be
            changed.
          </span>
        </div>
      ) : null}

      {/* The deadline lives in the panel above, said once. It only escalates to
          a notice here when it has already passed and the roster is shut. */}
      {locksAt && !isCanceled && rosterClosed ? (
        <div className="manage-table__notice manage-table__notice--warning">
          <ClockIcon aria-hidden="true" />
          <span>
            The guest list closed {locksAt}, so seats can no longer be changed.
          </span>
        </div>
      ) : null}

      <div className="manage-table__section">
        <h3 className="manage-table__section-title">Guests</h3>
      </div>

      {!isCanceled ? (
        <p className="manage-table__hint">
          {table.seatsRemaining > 0
            ? "We'll email each guest their pass as soon as you add them. No need to fill every seat right now."
            : "Your table is full. You can still swap guests until the list closes."}
        </p>
      ) : null}

      <ul className="manage-table__seats" aria-label="Table guest list">
        {seats.map((seat) => (
          <TableSeatRow
            key={seat.position}
            seat={seat}
            tableCode={table.code}
            token={token || null}
            onChanged={handleSeatChanged}
          />
        ))}
      </ul>
    </div>
  );
};

export default ManageTable;
